import { useState, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import {
  Page,
  PageTitleWithButton,
  Table,
  Pagination,
  Loader,
  MobileTable,
  SearchSection
} from '../../../components';
import { useQuery } from 'react-query';
import { Box, Button, useMediaQuery } from '@mui/material';
import { Edit, Add, DeleteForever } from '@mui/icons-material';
import { formatInputPhoneNumber } from '../../../helpers';
import { deleteWarrantyUser } from '../../../fetch';
import { IResponse } from '../../../models';
import { WarrantyUserModal } from './warranty-users-modal';
import { WarrantyUsersFilters } from './page-filters';
import { getWarrantyUsers } from '../../../fetch/warranty-users';
import { IWarrantyUser } from '../../../models/warranty-users';

export const WarrantyUsersPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoadingWarrantyUser, setLoadingWarrantyUser] = useState<boolean>(false);
  const [isModalOpen, showWarrantyUserModal] = useState<boolean>(false);
  const [currentWarrantyUserId, setCurrentWarrantyUserId] = useState<string | number>(undefined);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [selectedSort, setSelectedSort] = useState<string>('Name');
  const [sortDirection, setSortDirection] = useState<{
    Name?: 'Asc' | 'Desc';
    DisplayName?: 'Asc' | 'Desc';
    ContactPhoneNumber?: 'Asc' | 'Desc';
    ActiveBuildCount?: 'Asc' | 'Desc';
  }>({
    Name: 'Asc'
  });
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [filters, setFilters] = useState<{
    search?: string;
    neighborhoodId?: string | number;
    territoryId?: string | number;
  } | null>({});
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string | number>('');
  const [selectedTerritoryId, setSelectedTerritoryId] = useState<string | number>('');
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');

  const isMobile = useMediaQuery('(max-width: 960px)');

  const {
    isLoading: isLoadingWarrantyUsers,
    data: warrantyUsersData,
    refetch: refetchWarrantyUsers
  } = useQuery<IResponse<IWarrantyUser[]>, Error>(
    ['warrantyUsers', page, perPage, selectedSort, sortDirection, filters, searchedValue],
    () =>
      getWarrantyUsers({
        page: page + 1,
        perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        searchTerm: searchedValue || filters?.search,
        territoryId: filters?.territoryId,
        neighborhoodId: filters?.neighborhoodId
      }),
    {
      onSuccess: data => {
        setRecordCount(data.totalRecordCount);
      },
      notifyOnChangeProps: 'tracked'
    }
  );
  const warrantyUsers = useMemo(() => warrantyUsersData?.records ?? [], [warrantyUsersData]);

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const handleEdit = async (id: string | number) => {
    setCurrentWarrantyUserId(id);
    showWarrantyUserModal(true);
  };
  const handleDelete = async (id: string | number) => {
    const result = window.confirm('Are you sure you want to delete this warranty user?');

    if (result) {
      setLoadingWarrantyUser(true);
      try {
        await deleteWarrantyUser(id);
        refetchWarrantyUsers();
        enqueueSnackbar('Warranty user removed!', {
          variant: 'success'
        });
      } catch (error) {
        enqueueSnackbar(error?.Detail ?? `Error deleting warranty user, please try again.`, {
          variant: 'error'
        });
      } finally {
        setLoadingWarrantyUser(false);
      }
    }
  };
  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        isServerSorted: selectedSort === 'Name',
        isServerSortedDesc: sortDirection.Name === 'Desc',
        handleClickColumn: () => handleClickColumn('Name')
      },
      {
        Header: 'Display Name',
        accessor: 'displayName',
        isServerSorted: selectedSort === 'DisplayName',
        isServerSortedDesc: sortDirection.DisplayName === 'Desc',
        handleClickColumn: () => handleClickColumn('DisplayName')
      },
      {
        Header: 'Territories',
        accessor: 'territories',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IWarrantyUser } };
        }) => {
          return <>{original.territories.join(', ')}</>;
        }
      },
      {
        Header: 'Neighborhoods',
        accessor: 'neighborhoods',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IWarrantyUser } };
        }) => {
          return <>{original.neighborhoods.join(', ')}</>;
        }
      },
      {
        Header: 'Contact Number',
        accessor: 'contactPhoneNumber',
        isServerSorted: selectedSort === 'ContactPhoneNumber',
        isServerSortedDesc: sortDirection.ContactPhoneNumber === 'Desc',
        handleClickColumn: () => handleClickColumn('ContactPhoneNumber'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IWarrantyUser } };
        }) => {
          return <>{formatInputPhoneNumber(original.contactPhoneNumber)}</>;
        }
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        overrideWidth: 200,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IWarrantyUser } };
        }) => {
          return (
            <Box display="flex">
              <Button
                id="edit"
                color="primary"
                startIcon={<Edit />}
                onClick={async () => {
                  handleEdit(original.warrantyUserId);
                }}
              >
                Edit
              </Button>
              <Button
                id="delete"
                color="error"
                startIcon={<DeleteForever />}
                onClick={async () => {
                  console.log(original);
                  handleDelete(original.warrantyUserId);
                }}
              >
                Delete
              </Button>
            </Box>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warrantyUsers]);
  return (
    <>
      {isLoadingWarrantyUser && (
        <Loader position="centered" type="fullscreen">
          Loading...
        </Loader>
      )}
      <Page title="Warranty Managers">
        <PageTitleWithButton
          pageTitleProps={{
            title: 'Warranty Managers',
            showDivider: false
          }}
          button={
            <Button
              startIcon={<Add />}
              onClick={() => showWarrantyUserModal(true)}
              color="primary"
              variant="contained"
            >
              Add New Warranty Manager
            </Button>
          }
        />
        <Box mt={1}>
          <WarrantyUsersFilters
            isLoading={isLoadingWarrantyUsers}
            setHasAppliedFilters={setHasAppliedFilters}
            hasAppliedFilters={hasAppliedFilters}
            setSearchValue={setSearchValue}
            setSearchedValue={setSearchedValue}
            searchValue={searchValue}
            setSelectedNeighborhoodId={setSelectedNeighborhoodId}
            selectedNeighborhoodId={selectedNeighborhoodId}
            setSelectedTerritoryId={setSelectedTerritoryId}
            selectedTerritoryId={selectedTerritoryId}
            handleSearch={(val: string) => {
              setPage(0);
              setSearchedValue(val);
            }}
            applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
              if (clearFilters) {
                setFilters({
                  search: '',
                  neighborhoodId: undefined,
                  territoryId: undefined
                });
              } else {
                setPage(0);
                setFilters({
                  ...filters,
                  search: searchValue,
                  neighborhoodId: selectedNeighborhoodId || undefined,
                  territoryId: selectedTerritoryId || undefined
                });
              }
            }}
          />
          {searchedValue && (
            <SearchSection
              searchedValue={searchedValue}
              handleClearSearch={() => {
                setSearchValue('');
                setSearchedValue('');
                setHasAppliedFilters(false);
                setPage(0);
              }}
            />
          )}
        </Box>
        <Table
          columns={columns}
          data={warrantyUsers}
          isLoading={isLoadingWarrantyUsers}
          hidePagination
          mobileProps={{
            handleEdit: (val: IWarrantyUser) => {
              handleEdit(val.warrantyUserId);
            },
            handleDelete: (val: IWarrantyUser) => {
              handleDelete(val.warrantyUserId);
            },
            fields: columns
              .filter(c => c.Header)
              .map(c => ({
                name: c.Header,
                accessor: c.accessor,
                Cell: c?.Cell
              }))
          }}
          ResponsiveComponent={isMobile ? MobileTable : undefined}
        />
        {!isLoadingWarrantyUsers && (
          <Pagination
            page={page}
            count={recordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        )}
        <WarrantyUserModal
          warrantyUserId={currentWarrantyUserId}
          open={isModalOpen}
          onClose={() => {
            showWarrantyUserModal(false);
            setTimeout(() => {
              setCurrentWarrantyUserId(undefined);
            }, 500);
          }}
          fetchWarrantyUsers={refetchWarrantyUsers}
        />
      </Page>
    </>
  );
};
